// Screen Wake Lock — root-level Singleton-Manager + React-Bindings.
//
// Warum nicht einfach useEffect mit navigator.wakeLock.request?
//   iOS Safari verlangt für wakeLock.request('screen') "transient user
//   activation" — der Call muss innerhalb eines User-Gestures laufen
//   (Tap/Click/Keydown). useEffect feuert beim Mount ohne Gesture →
//   iOS verweigert still (kein throw, Promise resolved mit einem sofort-
//   releaseten Sentinel ODER wirft NotAllowedError, je nach Version).
//   Chrome/Firefox sind da gutmütig und geben den Lock auch ohne Gesture.
//
// Architektur:
//   - Ein Singleton-Manager (kein React-State) hält das Sentinel. Das
//     vermeidet Probleme mit doppelten Sentinels wenn der Hook mehrfach
//     läuft und macht den Status über useSyncExternalStore global
//     subscribable.
//   - setEnabled(true) startet Listener. pointerdown (capture) ist unsere
//     primäre Retry-Quelle auf iOS: JEDER Tap in der App ist eine
//     Gelegenheit für transient activation. acquire() ist idempotent —
//     wenn schon ein Sentinel existiert, passiert nix.
//   - visibilitychange → visible ist die Re-Acquire-Quelle wenn iOS beim
//     Backgrounden released hat. Meistens ist der Foreground-Event
//     selbst ein impliziter User-Gesture-Kontext; wenn nicht, greift
//     der nächste Tap.
//   - Die capture-Phase beim pointerdown-Listener ist WICHTIG: unsere
//     LocateControl ruft stopPropagation auf ihrem Button-pointerdown auf
//     (gegen iOS-Phantom-Pan, siehe LocateControl.ts). Ohne capture
//     würde der Listener diese Taps nie sehen — und das sind vermutlich
//     die häufigsten Interaktionen auf der Karten-Seite.

import { useEffect, useSyncExternalStore } from 'react';

export function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

export interface WakeLockStatus {
  /** Es liegt aktuell ein Sentinel vor (Screen bleibt wach). */
  active: boolean;
  /** Die Browser-API existiert überhaupt. */
  supported: boolean;
  /** enabled=true aber (noch) kein Sentinel — UI kann "wartet auf Tap" zeigen. */
  pending: boolean;
}

type Listener = () => void;

class WakeLockManager {
  private sentinel: WakeLockSentinel | null = null;
  private enabled = false;
  private readonly listeners = new Set<Listener>();
  private pointerdownAttached = false;
  private visibilityAttached = false;

  get supported(): boolean {
    return isWakeLockSupported();
  }

  get active(): boolean {
    return this.sentinel !== null;
  }

  get pending(): boolean {
    return this.enabled && this.supported && !this.sentinel;
  }

  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    if (on) {
      this.attachGlobalListeners();
      // Erster Versuch — klappt auf Desktop und bei iOS-Foreground oft schon.
      // Falls nicht: der nächste Tap löst einen Retry aus.
      void this.acquire();
    } else {
      this.detachGlobalListeners();
      void this.release();
    }
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    // lastSnapshot invalidieren damit useSyncExternalStore neu rechnet.
    lastSnapshot = null;
    for (const l of this.listeners) l();
  }

  private attachGlobalListeners(): void {
    if (!this.pointerdownAttached) {
      // capture:true — wir wollen JEDEN Tap sehen, auch die mit stopPropagation
      // (z. B. unser eigener LocateControl-Button).
      document.addEventListener('pointerdown', this.onPointerDown, { capture: true });
      this.pointerdownAttached = true;
    }
    if (!this.visibilityAttached) {
      document.addEventListener('visibilitychange', this.onVisibility);
      this.visibilityAttached = true;
    }
  }

  private detachGlobalListeners(): void {
    if (this.pointerdownAttached) {
      document.removeEventListener('pointerdown', this.onPointerDown, { capture: true });
      this.pointerdownAttached = false;
    }
    if (this.visibilityAttached) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.visibilityAttached = false;
    }
  }

  private readonly onPointerDown = (): void => {
    // Jeder Tap = Gelegenheit für transient activation auf iOS.
    // Idempotent, siehe acquire() early-return.
    void this.acquire();
  };

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      void this.acquire();
    }
  };

  private async acquire(): Promise<void> {
    if (!this.enabled) return;
    if (!this.supported) return;
    if (this.sentinel) return;
    if (document.visibilityState !== 'visible') return;
    try {
      const s = await navigator.wakeLock.request('screen');
      if (!this.enabled) {
        // Race: während des awaits hat jemand disabled → sofort wieder los.
        s.release().catch(() => undefined);
        return;
      }
      this.sentinel = s;
      console.info('[wakeLock] acquired');
      s.addEventListener('release', () => {
        if (this.sentinel === s) {
          this.sentinel = null;
          console.info('[wakeLock] released (by system)');
          this.notify();
          // iOS released beim Backgrounden — beim nächsten visibilitychange
          // (oder Tap) fordern wir wieder an, das läuft über die Listener.
        }
      });
      this.notify();
    } catch (err) {
      // iOS ohne Gesture: typisch NotAllowedError. Kein Drama — der nächste
      // pointerdown versucht's erneut.
      console.warn('[wakeLock] acquire failed (will retry on next tap)', err);
    }
  }

  private async release(): Promise<void> {
    const s = this.sentinel;
    this.sentinel = null;
    if (s) {
      try {
        await s.release();
      } catch {
        /* ignore */
      }
      console.info('[wakeLock] released (explicit)');
      this.notify();
    }
  }
}

const manager = new WakeLockManager();

// Snapshot-Caching für useSyncExternalStore:
// React ruft getSnapshot u. U. mehrfach pro Render auf und verlangt dann
// referenz-stabilen Output. Wir cachen bis die nächste notify() den Cache
// invalidiert.
let lastSnapshot: WakeLockStatus | null = null;
function getSnapshot(): WakeLockStatus {
  if (lastSnapshot) return lastSnapshot;
  lastSnapshot = {
    active: manager.active,
    supported: manager.supported,
    pending: manager.pending,
  };
  return lastSnapshot;
}
function getServerSnapshot(): WakeLockStatus {
  return { active: false, supported: false, pending: false };
}

/**
 * Hält den Bildschirm wach, solange `enabled === true` und die Seite sichtbar ist.
 *
 * ACHTUNG: Nur EINMAL pro App aufrufen (Root-Level in App.tsx). Mehrere Aufrufer
 * mit widersprüchlichen `enabled`-Werten würden den Sentinel ständig toggeln.
 * Für reine Status-Anzeige in anderen Components: useWakeLockStatus() nehmen.
 */
export function useWakeLock(enabled: boolean): WakeLockStatus {
  useEffect(() => {
    manager.setEnabled(enabled);
    return () => {
      // Beim Unmount (App-Teardown oder enabled-Wechsel) freigeben.
      // Bei enabled-Wechsel setzt der neue Effect-Run es direkt wieder — also ok.
      manager.setEnabled(false);
    };
  }, [enabled]);

  return useWakeLockStatus();
}

/** Pure Read-only Hook für Status-Anzeige in Components (z. B. ProfilePage). */
export function useWakeLockStatus(): WakeLockStatus {
  return useSyncExternalStore(
    (cb) => manager.subscribe(cb),
    getSnapshot,
    getServerSnapshot,
  );
}
